import { useEffect, useState } from 'react';
import api from '../../api/axios';

export default function SubCompanyForm({
  mode = 'create',
  mainCompanyId = 0,
  initial = null,
  onClose = () => { },
  onCreated = () => { }
}) {
  const [companyName, setCompanyName] = useState(initial?.company_name || '');
  const [stateId, setStateId] = useState(initial?.stateid || 0);
  const [countyId, setCountyId] = useState(initial?.countyid || 0);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [, setLoadingStates] = useState(false);
  const [, setLoadingCounties] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // add near top of SubCompanyForm
  const parseBool = (v) => {
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === '1' || s === 'true' || s === 'yes') return true;
      if (s === '0' || s === 'false' || s === 'no' || s === '') return false;
    }
    return Boolean(v);
  };

  // initial state (safe default)
  const [state_based_tax, setstate_based_tax] = useState(true);
  const [county_based_tax, setcounty_based_tax] = useState(() => parseBool(initial?.county_based_tax ?? initial?.county_level_tax ?? false));

  // keep local state in sync when `initial` changes
  useEffect(() => {
    if (mode === 'edit' && initial) {
      setcounty_based_tax(parseBool(initial.county_based_tax ?? initial.county_level_tax ?? false));
      setstate_based_tax(true);
    }
  }, [initial, mode]);

  const [errors, setErrors] = useState({});

  // State
  const [propertyTaxRows, setPropertyTaxRows] = useState([{ name: 'Annual Property Tax' }]);

  // Sync when initial changes (edit mode)
  useEffect(() => {
    if (mode === 'edit' && initial?.property_tax && Array.isArray(initial.property_tax)) {
      // Edit: load from server
      setPropertyTaxRows(initial.property_tax);
    } else if (mode === 'create') {
      // Create: always start with default row
      setPropertyTaxRows([{ name: 'Annual Property Tax' }]);
    }
  }, [initial, mode]);


  // Load all states
  useEffect(() => {
    // In edit mode we don't call the states API (listing provides statename)
    let mounted = true;
    const load = async () => {
      setLoadingStates(true);
      try {
        if (mode !== 'edit') {
          setStateId(0);
          setCountyId(0);
          const params = {};
          if (mainCompanyId) params.maincompanyid = mainCompanyId;
          const resp = await api.get('/auth/states/all', { params });
          const body = resp.data;
          const items = Array.isArray(body)
            ? body
            : Array.isArray(body.data)
              ? body.data
              : [];
          if (mounted) setStates(items);
        } else {
          // edit mode: prefer initial state id if provided
          const initState = Number(initial?.stateid || initial?.state_id || initial?.stateId) || 0;
          if (initState) setStateId(initState);
          // do not fetch states from API in edit mode
        }
      } catch (e) {
        console.error('Failed to load states', e?.response?.data || e?.message);
        if (mounted) setStates([]);
      } finally {
        if (mounted) setLoadingStates(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [mainCompanyId, initial, mode]);

  // Load counties when stateId changes
  useEffect(() => {
    // In edit mode we do not call the counties API; listing provides countyname
    let mounted = true;
    const load = async () => {
      if (mode === 'edit') {
        // ensure countyId is set from initial when editing; do not fetch
        const initCounty = Number(initial?.countyid || initial?.county_id || initial?.countyId) || 0;
        if (initCounty) setCountyId(initCounty);
        return;
      }
      if (!stateId) return setCounties([]);
      setLoadingCounties(true);
      try {
        const params = { stateId };
        if (mainCompanyId) params.maincompanyid = mainCompanyId;
        const resp = await api.get('/auth/counties/all', { params });
        const body = resp.data;
        const items = Array.isArray(body)
          ? body
          : Array.isArray(body.data)
            ? body.data
            : [];
        if (mounted) {
          setCounties(items);
        }
      } catch (e) {
        console.error('Failed to load counties', e?.response?.data || e?.message);
        if (mounted) setCounties([]);
      } finally {
        if (mounted) setLoadingCounties(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [stateId, initial, mainCompanyId, mode]);

  const validate = () => {
    const errs = {};
    if (!stateId) errs.state = 'State is required';
    if (!countyId) errs.county = 'County is required';
    if (!companyName || !companyName.trim())
      errs.company = 'Company name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        company_name: companyName.trim(),
        stateid: stateId,
        countyid: countyId,
        maincompanyid: mainCompanyId,
        state_based_tax: state_based_tax,
        county_based_tax: county_based_tax,
        property_tax: propertyTaxRows // ✅ plain array
      };

      if (mode === 'create') {
        await api.post('/admin/sub-companies', body);
      } else {
        if (!initial?.id) throw new Error('Missing id for edit');
        await api.put(`/admin/sub-companies/${initial.id}`, body);
      }
      onCreated();
    } catch (err) {
      console.error('SubCompany save error', err?.response?.data || err?.message);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Failed to save';
      setErrors((prev) => ({ ...prev, form: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* State */}
      <div>
        <label className="block text-sm">State</label>
        {mode === 'create' ? (
          <select
            value={stateId}
            onChange={(e) => setStateId(Number(e.target.value) || 0)}
            className="w-full border px-2 py-1 rounded text-black bg-white"
          >
            <option value={0}>Select State</option>
            {states.map((s) => (
              <option
                key={s.id || s.stateid || s.value}
                value={s.id || s.stateid || s.value}
              >
                {s.statename || s.name || s.label || s.state}
              </option>
            ))}
          </select>
        ) : (
          (() => {
            const sel = states.find((s) => Number(s.id || s.stateid || s.value) === Number(stateId));
            const label = sel ? (sel.statename || sel.name || sel.label || sel.state) : (initial?.statename || initial?.state_name || initial?.state || '');
            return (
              <input readOnly className="w-full border px-2 py-1 rounded bg-gray-100 text-black" value={label || '—'} />
            );
          })()
        )}
        {errors.state && (
          <div className="text-red-600 text-xs">{errors.state}</div>
        )}
      </div>

      {/* County */}
      <div>
        <label className="block text-sm">County</label>
        {mode === 'create' ? (
          <select
            value={countyId}
            onChange={(e) => setCountyId(Number(e.target.value) || 0)}
            className="w-full border px-2 py-1 rounded text-black bg-white"
          >
            <option value={0}>Select County</option>
            {counties.map((c) => (
              <option
                key={c.id || c.countyid || c.value}
                value={c.id || c.countyid || c.value}
              >
                {c.countyname || c.name || c.label || c.county}
              </option>
            ))}
          </select>
        ) : (
          (() => {
            const sel = counties.find((c) => Number(c.id || c.countyid || c.value) === Number(countyId));
            const label = sel ? (sel.countyname || sel.name || sel.label || sel.county) : (initial?.countyname || initial?.county_name || initial?.county || '');
            return (
              <input readOnly className="w-full border px-2 py-1 rounded bg-gray-100 text-black" value={label || '—'} />
            );
          })()
        )}
        {errors.county && (
          <div className="text-red-600 text-xs">{errors.county}</div>
        )}
      </div>

      {/* Company Name */}
      <div>
        <label className="block text-sm">Company Name</label>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          className="w-full border px-2 py-1 rounded"
        />
        {errors.company && (
          <div className="text-red-600 text-xs">{errors.company}</div>
        )}
      </div>

      {/* State Level Tax 
      <div>
        <label className="block text-sm mb-1">State based Tax</label>
        <input
          type="checkbox"
          checked={state_based_tax}
          onChange={(e) => setstate_based_tax(e.target.checked)}
        />
        <span className="ml-2">Applicable</span>
      </div>*/}

      {/* County Level Tax */}
      <div>
        <label className="block text-sm mb-1">County based Tax Setting</label>
        <input
          type="checkbox"
          checked={county_based_tax}
          onChange={(e) => setcounty_based_tax(e.target.checked)}
        />
        <span className="ml-2">Applicable</span>
      </div>

      {/* Property Tax */}
      <div>
        <label className="block text-sm mb-1">Property Tax</label>

        {propertyTaxRows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={row?.name || ''}
              onChange={(e) => {
                const updated = [...propertyTaxRows];
                updated[idx] = { name: e.target.value };
                setPropertyTaxRows(updated);
              }}
              className="flex-1 border px-2 py-1 rounded"
              placeholder={`Property Tax ${idx + 1}`}
            />
            {idx > 0 && (
              <button
                type="button"
                onClick={() => {
                  const updated = propertyTaxRows.filter((_, i) => i !== idx);
                  setPropertyTaxRows(updated);
                }}
                className="px-2 py-1 text-xs bg-red-500 text-white rounded"
              >
                Remove
              </button>
            )}
          </div>
        ))}

        {/* Add Row Button (max 2 rows) */}
        {propertyTaxRows.length < 2 && (
          <button
            type="button"
            onClick={() =>
              setPropertyTaxRows([...propertyTaxRows, { name: '' }])
            }
            className="px-2 py-1 text-xs bg-green-500 text-white rounded"
          >
            + Add Row
          </button>
        )}
      </div>



      {/* Buttons */}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 border rounded"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-1 bg-blue-600 text-white rounded"
        >
          {submitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? 'Create'
              : 'Save'}
        </button>
      </div>

      {errors.form && (
        <div className="text-red-600 text-sm mt-2">{errors.form}</div>
      )}
    </form>
  );
}
